import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { useLang } from "../../i18n";
import { IMAGES, scrollTo } from "../../utils";

const MaskedLine = ({ text, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {text}
    </motion.span>
  </span>
);

const Hero = () => {
  const { t } = useLang();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 220]);
  const opacity = useTransform(scrollY, [0, 600], [1, 0.3]);

  return (
    <section id="top" className="relative min-h-screen flex flex-col justify-end overflow-hidden noise-overlay" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y }}>
        <video
          className="w-full h-[120%] object-cover"
          poster={IMAGES.hero}
          autoPlay
          muted
          loop
          playsInline
          data-testid="hero-video"
        >
          <source src="/assets/hero-video.webm" type="video/webm" />
          <source src="/assets/hero-video.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-[#0A0B0E]/75" />
        <div className="absolute inset-0 blueprint-grid" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 px-6 lg:px-12 pt-40 pb-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="flex items-center gap-4 mb-8"
        >
          <span className="h-px w-14 bg-[#FF4D00]" />
          <span className="font-mono text-xs uppercase tracking-[0.35em] text-[#A0AABF]" data-testid="hero-overline">
            {t.hero.overline}
          </span>
        </motion.div>

        <h1 className="font-display font-extrabold tracking-tighter text-white leading-[0.9] text-[15vw] sm:text-7xl lg:text-8xl xl:text-[7.5rem]">
          <MaskedLine text={t.hero.lines[0]} delay={0.25} />
          <span className="text-[#4D8BFF]">
            <MaskedLine text={t.hero.lines[1]} delay={0.4} />
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="text-base md:text-lg text-[#A0AABF] max-w-2xl mt-8 leading-relaxed"
          data-testid="hero-subtitle"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="flex flex-wrap gap-4 mt-10"
        >
          <button
            data-testid="hero-quote-btn"
            onClick={() => scrollTo("#devis")}
            className="group flex items-center gap-3 bg-[#FF4D00] text-white font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 hover:bg-[#0055FF] transition-colors"
          >
            {t.hero.ctaPrimary}
            <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </button>
          <button
            data-testid="hero-products-btn"
            onClick={() => scrollTo("#produits")}
            className="group flex items-center gap-3 border border-[#232733] text-white font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 hover:border-[#0055FF] hover:text-[#4D8BFF] transition-colors"
          >
            {t.hero.ctaSecondary}
            <ArrowDown size={16} className="transition-transform group-hover:translate-y-1" />
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1.05 }}
        className="relative z-10 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#232733] border-t border-[#232733]"
        data-testid="hero-stats"
      >
        {t.hero.stats.map((s, i) => (
          <div key={i} className="bg-[#0A0B0E]/90 backdrop-blur px-6 lg:px-10 py-6 group hover:bg-[#111318] transition-colors">
            <div className="font-display text-2xl lg:text-3xl font-medium tracking-tight text-white group-hover:text-[#4D8BFF] transition-colors">
              {s.value}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#647087] mt-2">{s.label}</div>
          </div>
        ))}
      </motion.div>
    </section>
  );
};

export default Hero;
